// The structure of a DAG skill: its schema, the warnings shown while
// editing it, and the markdown that agents read.

import { z } from "zod/v4";
import {
  DAG_TYPE_FIELDS,
  DAG_VALUE_TYPES,
  type DagItemType,
  type DagValueType,
  dagItemOf,
  dagTypeName,
  dagTypeOf,
  describeDagType,
} from "./value-types";

/**
 * What a step id, output, input or loop item name must look like. Steps
 * reference them from text as `{{name}}`, so they stay identifier-shaped.
 */
export const DAG_IDENTIFIER_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
/** Why a name doesn't match `DAG_IDENTIFIER_PATTERN`, to show the person who typed it. */
export const DAG_IDENTIFIER_MESSAGE =
  "Use lowercase letters, numbers and underscores, starting with a letter";

const identifierSchema = z
  .string()
  .regex(DAG_IDENTIFIER_PATTERN, DAG_IDENTIFIER_MESSAGE);

// Everything here is rendered into the agent's prompt and stored on every
// version, and the agent can write it, so each part has a ceiling.
const MAX_TEXT_LENGTH = 10_000;
const MAX_INPUTS = 50;
const MAX_STEPS = 100;

/** The input types that can have a default, since a default is written as text. */
export const DAG_DEFAULTABLE_TYPES: readonly DagValueType[] = ["text", "date"];

const itemTypeSchema: z.ZodType<DagItemType> = z.lazy(() =>
  z.union([
    z.enum(DAG_VALUE_TYPES),
    z.object({ type: z.literal("list"), of: itemTypeSchema }),
  ]),
);

const inputSchema = z
  .object({
    name: identifierSchema,
    type: z.enum(DAG_VALUE_TYPES),
    // For a list: what each item is, so a loop over it knows its item's fields.
    of: itemTypeSchema.optional(),
    description: z.string().max(500).optional(),
    // What the run uses when the input isn't given. Without one the input is required: the agent works it out from context first, and only if it can't does it ask a person who is there, or report what is missing.
    default: z.string().max(500).optional(),
  })
  .superRefine((input, ctx) => {
    if (input.of !== undefined && input.type !== "list") {
      ctx.addIssue({
        code: "custom",
        message: `${input.name} is a ${input.type}, so it has no items to give a type`,
      });
    }
    if (input.default === undefined) return;
    if (!DAG_DEFAULTABLE_TYPES.includes(input.type)) {
      ctx.addIssue({
        code: "custom",
        message: `${input.name} is a ${input.type}, which can't have a default`,
      });
    }
  });

const actionStepSchema = z.object({
  kind: z.literal("step"),
  id: identifierSchema,
  instructions: z.string().max(MAX_TEXT_LENGTH),
  output: identifierSchema.optional(),
  // What the output holds, written like an input's type, so later steps and loops know its fields.
  outputType: z.enum(DAG_VALUE_TYPES).optional(),
  outputOf: itemTypeSchema.optional(),
  // Plain language the agent evaluates; the step is skipped when it's false.
  onlyIf: z.string().max(MAX_TEXT_LENGTH).optional(),
});

const forEachStepSchema = z.object({
  kind: z.literal("for_each"),
  id: identifierSchema,
  // The list to loop over, as text. A reference is written in braces, such as
  // `{{project.tasks}}`, and anything outside braces describes the list.
  in: z
    .string()
    .trim()
    .min(1, "Choose the list this loop goes through")
    .max(MAX_TEXT_LENGTH),
  as: identifierSchema,
  // Loops don't nest, so a loop body holds only action steps.
  steps: z.array(actionStepSchema).max(MAX_STEPS),
});

// Both end a run's progress until a person or a condition says otherwise, so
// like loops they sit at the top level only.
const approvalStepSchema = z.object({
  kind: z.literal("approval"),
  id: identifierSchema,
  // What the person approving is asked.
  message: z.string().max(MAX_TEXT_LENGTH),
  onlyIf: z.string().max(MAX_TEXT_LENGTH).optional(),
});

const stopStepSchema = z.object({
  kind: z.literal("stop"),
  id: identifierSchema,
  // Why the run ends here.
  reason: z.string().max(MAX_TEXT_LENGTH),
  onlyIf: z.string().max(MAX_TEXT_LENGTH).optional(),
});

const stepSchema = z.discriminatedUnion("kind", [
  actionStepSchema,
  forEachStepSchema,
  approvalStepSchema,
  stopStepSchema,
]);

/** The steps of a DAG skill, as the builder and the agent edit them. */
export const DagSchema = z
  .object({
    version: z.literal(1),
    // Shown to the agent before the steps; applies to every step.
    guidance: z.string().max(MAX_TEXT_LENGTH),
    inputs: z.array(inputSchema).max(MAX_INPUTS),
    steps: z.array(stepSchema).max(MAX_STEPS),
  })
  .superRefine((content, ctx) => {
    const seen = new Set<string>();
    for (const id of allStepIds(content.steps)) {
      if (seen.has(id)) {
        ctx.addIssue({ code: "custom", message: `Duplicate step id ${id}` });
      }
      seen.add(id);
    }
    for (const step of allActionSteps(content.steps)) {
      if (step.outputType !== undefined && step.output === undefined) {
        ctx.addIssue({
          code: "custom",
          message: "Name the output to give it a type",
        });
      }
      if (step.outputOf !== undefined && step.outputType !== "list") {
        ctx.addIssue({
          code: "custom",
          message: `${step.output ?? "This output"} isn't a list, so it has no items to give a type`,
        });
      }
    }
    const names = new Set<string>();
    for (const name of [
      ...content.inputs.map((input) => input.name),
      ...allOutputNames(content.steps),
      ...allLoopItemNames(content.steps),
    ]) {
      if (names.has(name)) {
        ctx.addIssue({
          code: "custom",
          message: `${name} is used for more than one input, output or loop item`,
        });
      }
      names.add(name);
    }
  });

export type Dag = z.infer<typeof DagSchema>;
/** A top-level step: an action, a loop over actions, an approval gate, or a stop. */
export type DagStep = z.infer<typeof stepSchema>;
/** A step that does one thing, at the top level or inside a loop. */
export type DagActionStep = z.infer<typeof actionStepSchema>;
/** A step that waits for a person to approve before the run goes on. */
export type DagApprovalStep = z.infer<typeof approvalStepSchema>;
/** A step that ends the run. */
export type DagStopStep = z.infer<typeof stopStepSchema>;

/** The dag of a new DAG skill, with no inputs or steps. */
export function emptyDag(): Dag {
  return { version: 1, guidance: "", inputs: [], steps: [] };
}

function allStepIds(steps: readonly DagStep[]): string[] {
  return steps.flatMap((step) =>
    step.kind === "for_each"
      ? [step.id, ...step.steps.map((inner) => inner.id)]
      : [step.id],
  );
}

function allLoopItemNames(steps: readonly DagStep[]): string[] {
  return steps.flatMap((step) => (step.kind === "for_each" ? [step.as] : []));
}

function allActionSteps(steps: readonly DagStep[]): DagActionStep[] {
  return steps.flatMap((step) => {
    if (step.kind === "for_each") return step.steps;
    return step.kind === "step" ? [step] : [];
  });
}

/** The type an action step's output was given, when it was given one. */
export function dagOutputType(step: DagActionStep): DagItemType | undefined {
  return step.outputType === undefined
    ? undefined
    : dagTypeOf(step.outputType, step.outputOf);
}

function allOutputNames(steps: readonly DagStep[]): string[] {
  return steps.flatMap((step) => {
    if (step.kind === "for_each") {
      return step.steps.flatMap((inner) =>
        inner.output ? [inner.output] : [],
      );
    }
    return step.kind === "step" && step.output ? [step.output] : [];
  });
}

/** A `{{path}}` reference in step text; group 1 is the path. */
export const REFERENCE_PATTERN = /\{\{\s*([a-zA-Z_][\w.]*)\s*\}\}/g;

/** A loop's list that is exactly one reference, such as `{{project.tasks}}`; group 1 is the path. */
export const WHOLE_REFERENCE_PATTERN = /^\{\{\s*([a-zA-Z_][\w.]*)\s*\}\}$/;

// The paths (`project.tasks` for `{{project.tasks}}`) referenced in a piece of text.
function referencedPaths(text: string): string[] {
  return [...text.matchAll(REFERENCE_PATTERN)].flatMap((match) =>
    match[1] ? [match[1]] : [],
  );
}

// Follows a typed value's fields down a path and says where it ends. A list is
// walked by number into its items when it says what they are, and otherwise
// the walk stops there without a warning.
function walkType(
  root: string,
  type: DagItemType,
  segments: readonly string[],
): { warnings: string[]; type: DagItemType | undefined } {
  let current = type;
  let walked = root;
  for (const segment of segments) {
    const name = dagTypeName(current);
    if (name === "list") {
      const item = dagItemOf(current);
      if (item === undefined || !/^\d+$/.test(segment)) {
        return { warnings: [], type: undefined };
      }
      current = item;
    } else {
      const known = DAG_TYPE_FIELDS[name];
      const field = known.find((candidate) => candidate.path === segment);
      if (!field) {
        return {
          warnings: [
            known.length === 0
              ? `${walked} is a ${name} and has no fields`
              : `${walked} has no field ${segment}`,
          ],
          type: undefined,
        };
      }
      current = dagTypeOf(field.type, field.of);
    }
    walked = `${walked}.${segment}`;
  }
  return { warnings: [], type: current };
}

// What each item of a loop's list is, when the list is one reference that says.
function loopItemType(
  list: string,
  types: ReadonlyMap<string, DagItemType>,
): DagItemType | undefined {
  const path = WHOLE_REFERENCE_PATTERN.exec(list.trim())?.[1];
  if (path === undefined) return undefined;
  const [root = "", ...fields] = path.split(".");
  const rootType = types.get(root);
  if (rootType === undefined) return undefined;
  const { type } = walkType(root, rootType, fields);
  return type === undefined ? undefined : dagItemOf(type);
}

/**
 * How a skill is named in a `/skill` token: the same rule the agent runtime
 * uses for skill folders, so a token can name the skill it loads.
 */
export function skillSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

/** A problem shown on a step; the skill still saves. */
export type DagWarning = { stepId: string | null; message: string };

/**
 * References that won't resolve where they're used. Warnings never block a
 * save: one agent reads the whole skill and can usually recover.
 */
export function collectDagWarnings(content: Dag): DagWarning[] {
  const warnings: DagWarning[] = [];
  const laterOutputs = new Set(allOutputNames(content.steps));
  // Inputs, then each typed output once its step has run.
  const types = new Map<string, DagItemType>(
    content.inputs.map((input) => [
      input.name,
      dagTypeOf(input.type, input.of),
    ]),
  );
  const available = new Set(types.keys());
  // Outputs a later step can't count on: saved inside a loop that has ended, or by a step that may be skipped.
  const closedLoopOutputs = new Set<string>();
  const conditionalOutputs = new Set<string>();

  const check = (
    stepId: string,
    text: string,
    scope: ReadonlySet<string>,
    scopeTypes: ReadonlyMap<string, DagItemType> = types,
  ) => {
    const warn = (message: string) => warnings.push({ stepId, message });
    for (const path of new Set(referencedPaths(text))) {
      const [root = "", ...fields] = path.split(".");
      if (!scope.has(root)) {
        warn(
          closedLoopOutputs.has(root)
            ? `${root} is saved inside a loop, so it isn't available after it`
            : laterOutputs.has(root)
              ? `${root} is saved by a later step, so it isn't available here`
              : `${root} isn't an input or an earlier output`,
        );
        continue;
      }
      if (conditionalOutputs.has(root)) {
        warn(
          `${root} is saved by a step that only runs sometimes, so it may not exist here`,
        );
      }
      const type = scopeTypes.get(root);
      if (type) {
        for (const message of walkType(root, type, fields).warnings) {
          warn(message);
        }
      }
    }
  };

  for (const step of content.steps) {
    if (step.kind === "step") {
      check(step.id, `${step.instructions} ${step.onlyIf ?? ""}`, available);
      if (step.output) {
        available.add(step.output);
        if (step.onlyIf) conditionalOutputs.add(step.output);
        const outputType = dagOutputType(step);
        if (outputType !== undefined) types.set(step.output, outputType);
      }
      continue;
    }
    if (step.kind === "approval" || step.kind === "stop") {
      const text = step.kind === "approval" ? step.message : step.reason;
      check(step.id, `${text} ${step.onlyIf ?? ""}`, available);
      continue;
    }
    check(step.id, step.in, available);
    const loopScope = new Set(available).add(step.as);
    const loopTypes = new Map(types);
    const itemType = loopItemType(step.in, types);
    if (itemType !== undefined) loopTypes.set(step.as, itemType);
    for (const inner of step.steps) {
      check(
        inner.id,
        `${inner.instructions} ${inner.onlyIf ?? ""}`,
        loopScope,
        loopTypes,
      );
      if (inner.output) {
        loopScope.add(inner.output);
        if (inner.onlyIf) conditionalOutputs.add(inner.output);
        const outputType = dagOutputType(inner);
        if (outputType !== undefined) loopTypes.set(inner.output, outputType);
      }
    }
    for (const inner of step.steps) {
      if (inner.output) closedLoopOutputs.add(inner.output);
    }
  }
  return warnings;
}

/** A skill a step names: its current name, and where the running agent finds its file when it has one. */
export type DagReference = { name: string; path?: string };
/** By entity id. An id the map lacks names a skill the reader can't use. */
export type DagReferences = ReadonlyMap<string, DagReference>;

/**
 * A skill named in step text as `/brand_check:<entity id>`. Group 1 is the
 * character before the slash, group 2 the token the author typed and group 3
 * the id that survives a rename. A `/token` without an id is plain text.
 */
export const SKILL_TOKEN_PATTERN =
  /(^|[^\w/-])\/([a-zA-Z0-9_-]{1,100}):([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?![\w-])/gi;

// Every free-text field of the steps, where a skill can be named.
function dagTexts(dag: Dag): string[] {
  const actionTexts = (step: DagActionStep) => [
    step.instructions,
    step.onlyIf ?? "",
  ];
  return [
    dag.guidance,
    ...dag.steps.flatMap((step) => {
      switch (step.kind) {
        case "step":
          return actionTexts(step);
        case "for_each":
          return [step.in, ...step.steps.flatMap(actionTexts)];
        case "approval":
          return [step.message, step.onlyIf ?? ""];
        case "stop":
          return [step.reason, step.onlyIf ?? ""];
      }
    }),
  ];
}

/** The skill entity ids the steps name, without repeats. */
export function dagReferencedEntityIds(dag: Dag): string[] {
  const ids = new Set<string>();
  for (const text of dagTexts(dag)) {
    for (const match of text.matchAll(SKILL_TOKEN_PATTERN)) {
      if (match[3]) ids.add(match[3]);
    }
  }
  return [...ids];
}

// Reads each `/token:id` as plain language. With references, a skill is named
// as its current name and file; without them it keeps the token and drops the
// id, so the text depends on the steps alone.
function renderText(
  text: string,
  references: DagReferences | undefined,
): string {
  return text
    .replace(
      SKILL_TOKEN_PATTERN,
      (_match, lead: string, token: string, id: string) => {
        if (!references) return `${lead}/${token}`;
        const reference = references.get(id);
        return reference
          ? `${lead}the skill "${reference.name}"${reference.path ? ` (file ${reference.path})` : ""}`
          : `${lead}/${token} (a skill that isn't available)`;
      },
    )
    .trim();
}

function renderActionStep(
  step: DagActionStep,
  references: DagReferences | undefined,
): string {
  const parts = [
    step.onlyIf ? `Only if ${renderText(step.onlyIf, references)}:` : null,
    renderText(step.instructions, references),
    step.output ? renderOutput(step.output, dagOutputType(step)) : null,
  ];
  return parts.filter((part) => part).join(" ");
}

function renderOutput(name: string, type: DagItemType | undefined): string {
  return type === undefined
    ? `Save the result as \`${name}\`.`
    : `Save the result as \`${name}\` (${describeDagType(type)}).`;
}

function renderInputType(input: Dag["inputs"][number]): string {
  return [
    describeDagType(dagTypeOf(input.type, input.of)),
    input.default !== undefined ? `defaults to "${input.default}"` : null,
  ]
    .filter((part) => part)
    .join(", ");
}

function renderTopLevelStep(
  step: DagStep,
  references: DagReferences | undefined,
): string | null {
  if (step.kind === "approval") {
    return [
      step.onlyIf ? `Only if ${renderText(step.onlyIf, references)}:` : null,
      `Stop and ask a person to approve before you go on: ${renderText(step.message, references)}`,
      "Go on only if they approve.",
    ]
      .filter((part) => part)
      .join(" ");
  }
  if (step.kind === "stop") {
    return [
      step.onlyIf ? `Only if ${renderText(step.onlyIf, references)}:` : null,
      `Stop here and skip the remaining steps: ${renderText(step.reason, references)}`,
    ]
      .filter((part) => part)
      .join(" ");
  }
  return null;
}

/**
 * The markdown stored beside the structure. Every agent that loads the skill
 * reads this, so the steps keep their meaning outside the builder.
 */
export function renderDagMarkdown(
  content: Dag,
  references?: DagReferences,
): string {
  const sections: string[] = [];
  if (content.guidance.trim()) {
    sections.push(renderText(content.guidance, references));
  }
  if (content.inputs.length > 0) {
    sections.push(
      [
        "## Inputs",
        ...content.inputs.map(
          (input) =>
            `- \`${input.name}\` (${renderInputType(input)})${
              input.description ? `: ${input.description}` : ""
            }`,
        ),
      ].join("\n"),
    );
  }
  if (content.steps.length > 0) {
    const lines = ["## Steps", "Do these in order."];
    content.steps.forEach((step, index) => {
      if (step.kind === "step") {
        lines.push(`${index + 1}. ${renderActionStep(step, references)}`);
        return;
      }
      if (step.kind !== "for_each") {
        lines.push(`${index + 1}. ${renderTopLevelStep(step, references)}`);
        return;
      }
      lines.push(
        `${index + 1}. For each \`${step.as}\` in ${renderText(step.in, references)}, do these in order:`,
      );
      step.steps.forEach((inner, innerIndex) => {
        lines.push(
          `   ${innerIndex + 1}. ${renderActionStep(inner, references)}`,
        );
      });
    });
    sections.push(lines.join("\n"));
  }
  return sections.join("\n\n");
}

/** Thrown when a skill's dag doesn't match the schema; the message is safe to show. */
export class DagValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DagValidationError";
  }
}

/** Parses a dag the way a save does, and throws on anything a save would refuse. */
export function parseDag(dag: unknown): Dag {
  const parsed = DagSchema.safeParse(dag);
  if (!parsed.success) {
    throw new DagValidationError(
      parsed.error.issues.map((issue) => issue.message).join("; "),
    );
  }
  return parsed.data;
}

/**
 * What the agent that edits a DAG skill reads in place of its markdown, which
 * is generated from the steps and adds nothing to them.
 */
export function renderDagForEditing(dag: unknown): string {
  return [
    "This skill is written as steps. To change it, propose the whole edited object as `content.dag`, with `content.markdown` as an empty string.",
    "",
    "```json",
    JSON.stringify(dag, null, 2),
    "```",
    "",
  ].join("\n");
}

/**
 * Skill content as it is stored. A skill with a dag gets its markdown
 * generated from it on every write, so agents that load the skill read its
 * steps and the two can't drift.
 */
export function withRenderedDagMarkdown<
  T extends { markdown: string; dag?: unknown },
>(content: T, references?: DagReferences): T {
  if (content.dag == null) return content;
  const dag = parseDag(content.dag);
  return { ...content, dag, markdown: renderDagMarkdown(dag, references) };
}
